/**
 * Environment Variable Verifier
 * 
 * This utility verifies that all required environment variables are properly set
 * and logs any issues to the console. It's designed to help diagnose deployment issues.
 */

// List of required environment variables
const REQUIRED_VARIABLES = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_KEY',
  'VITE_VAPI_PUBLIC_KEY'
];

/**
 * Verifies that all required environment variables are set
 * @returns {Object} Object containing verification results
 */
export const verifyEnvironment = () => {
  const results = {
    allVariablesPresent: true,
    missingVariables: [],
    variables: {}
  };

  // Check each required variable
  REQUIRED_VARIABLES.forEach(varName => {
    const value = import.meta.env[varName];
    const isPresent = Boolean(value);
    const isMasked = varName.includes('KEY') || varName.includes('TOKEN');
    
    results.variables[varName] = {
      isPresent,
      value: isMasked ? (isPresent ? '****' : undefined) : value
    };
    
    if (!isPresent) {
      results.allVariablesPresent = false;
      results.missingVariables.push(varName);
    }
  });

  return results;
};

/**
 * Logs environment verification results to the console
 */
export const logEnvironmentStatus = () => {
  const results = verifyEnvironment();
  
  console.group('Environment Variable Verification');
  console.log('All required variables present:', results.allVariablesPresent ? '✅ Yes' : '❌ No');
  
  if (!results.allVariablesPresent) {
    console.warn('Missing variables:', results.missingVariables.join(', '));
  }
  
  console.log('Variables status:');
  Object.entries(results.variables).forEach(([name, info]) => {
    console.log(`- ${name}: ${info.isPresent ? '✅ Present' : '❌ Missing'} ${info.value ? `(${info.value})` : ''}`);
  });
  
  console.groupEnd();
  
  return results;
};

/**
 * Initializes environment verification
 * This should be called early in the application startup
 */
export const initEnvironmentVerification = () => {
  const results = logEnvironmentStatus();
  
  // Add fallback values to window object if needed
  if (!results.allVariablesPresent) {
    console.warn('Setting fallback values for missing environment variables');
    
    // Fallback values for development
    const FALLBACK_SUPABASE_URL = 'https://utopqxsvudgrtiwenlzl.supabase.co';
    const FALLBACK_SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InV0b3BxeHN2dWRncnRpd2VubHpsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzg5NDgwMDcsImV4cCI6MjA1NDUyNDAwN30.CCv9bvV5Ee2r7A8TcFR4QXHFCNVo3QfiTz5nPzK4cRU';
    const FALLBACK_VAPI_KEY = '310f0d43-27c2-47a5-a76d-e55171d024f7';
    
    // Set fallback values in window object
    if (!import.meta.env.VITE_SUPABASE_URL) {
      window.VITE_SUPABASE_URL = FALLBACK_SUPABASE_URL;
    }
    
    if (!import.meta.env.VITE_SUPABASE_KEY) {
      window.VITE_SUPABASE_KEY = FALLBACK_SUPABASE_KEY;
    }
    
    if (!import.meta.env.VITE_VAPI_PUBLIC_KEY) {
      window.VITE_VAPI_PUBLIC_KEY = FALLBACK_VAPI_KEY;
    }
  }
  
  return results;
};

export default {
  verifyEnvironment,
  logEnvironmentStatus,
  initEnvironmentVerification
};
